"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import AvatarCharacter from "./AvatarCharacter";
import AvatarComposeModal from "./AvatarComposeModal";

const DISMISSED_KEY = "believemy_avatar_invite_dismissed";

// Petit bloc du tableau de bord : il invite à composer son avatar le membre qui n'en a
// pas, ou qui porte encore celui qu'on lui a attribué d'office (origin: "auto"). Il
// disparaît dès que l'avatar est enregistré, ou quand le membre le ferme.
// `preview` : l'affiche sans compte connecté ni mémoire de fermeture (page de prototype).
export default function AvatarInviteCard({ className = "", preview = false }) {
    const t = useTranslations();
    const { data: session, status } = useSession();
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(true);

    useEffect(() => {
        try {
            setDismissed(localStorage.getItem(DISMISSED_KEY) === "1");
        } catch {
            setDismissed(false);
        }
    }, []);

    const avatar = session?.user?.avatar;
    const composed = avatar && avatar.origin !== "auto";
    const hidden = preview ? false : status !== "authenticated" || composed || dismissed;

    const dismiss = () => {
        setDismissed(true);
        try {
            localStorage.setItem(DISMISSED_KEY, "1");
        } catch {}
    };

    return (
        <>
            {!hidden && (
                <div className={`relative flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-borderDark dark:bg-bgDark-800 ${className}`}>
                    <AvatarCharacter avatar={avatar || undefined} seed={`member-${session?.user?.userId}`} size={64} animated className="shrink-0" />
                    <div className="min-w-0 flex-1 pr-6">
                        <p className="mb-0.5 text-sm font-semibold text-gray-900 dark:text-white">{t("dashboard_avatar_invite_title")}</p>
                        <p className="mb-2.5 text-sm text-gray-500 dark:text-textSecondDark">{t("dashboard_avatar_invite_text")}</p>
                        <Button type="secondary" small onClick={() => setOpen(true)}>
                            {t("account_avatar_compose")}
                        </Button>
                    </div>
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label={t("dashboard_avatar_invite_dismiss")}
                        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/5 dark:hover:text-gray-200"
                    >
                        <SolarIcon name="close-circle-linear" className="text-lg" />
                    </button>
                </div>
            )}
            {/* La fenêtre reste montée : enregistrer fait disparaître le bloc pendant qu'elle se ferme. */}
            <AvatarComposeModal isOpen={open} onClose={() => setOpen(false)} />
        </>
    );
}
